import React, { useState } from "react";
import {
	Card,
	CardHeader,
	CardContent,
	Typography,
	Grid,
	CircularProgress,
	TextField,
	Switch,
} from "@material-ui/core";
import { usePermissions } from "ra-core";
import { useQuery } from "@apollo/client/react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import type {
	DashboardQuery,
	DashboardQueryVariables,
} from "~/features/dashboard/query.generated";
import dashboardQuery from "~/features/dashboard/query.graphql";
import {
	dateToAnalyticsDateFormat,
	newUsersChartOptions,
	TimeInMilliseconds,
	transformAnalyticsData,
	savedProjectsChartOptions,
	cartProjectsChartOptions,
	orderedProjectsChartOptions,
	combinedProjectsChartOptions,
} from "./utils.ts";

import { generateExampleData } from "./example-data.ts";

// Register ChartJS components
ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
);

// Set to true to use example data
const typedExampleData = generateExampleData({
	fromDate: new Date(Date.now() - TimeInMilliseconds.WEEK),
	toDate: new Date(),
}) as unknown as DashboardQuery;

function Analytics() {
	// State to control using example data
	const [useExampleData, setUseExampleData] = useState(false);
	const { permissions } = usePermissions();
	const [formValues, setFormValues] = useState({
		from: new Date(Date.now() - TimeInMilliseconds.WEEK),
		to: new Date(),
	});

	// Always call useQuery, but skip if USE_EXAMPLE_DATA is true
	const {
		data: queryData,
		loading,
		error,
		refetch,
	} = useQuery<DashboardQuery, DashboardQueryVariables>(dashboardQuery, {
		variables: {
			from: dateToAnalyticsDateFormat(formValues.from),
			to: dateToAnalyticsDateFormat(formValues.to),
		},
		skip: useExampleData, // Skip the query if using example data
	});

	// Determine the data source
	const data = useExampleData ? typedExampleData : queryData;
	const isDataLoading = useExampleData ? false : loading;
	const dataError = useExampleData ? undefined : error;

	const handleDateChange =
		(field: "from" | "to") => (event: React.ChangeEvent<HTMLInputElement>) => {
			const newDate = new Date(event.target.value);
			const newValues = {
				...formValues,
				[field]: newDate,
			};
			setFormValues(newValues);
			// Only refetch if not using example data and refetch function exists
			if (!useExampleData && refetch) {
				void refetch({
					from: dateToAnalyticsDateFormat(newValues.from),
					to: dateToAnalyticsDateFormat(newValues.to),
				});
			}
		};

	return (
		<Card>
			<CardHeader
				title="Analytics"
				action={
					<>
						<Typography component="span" variant="body2">
							Use Example Data
						</Typography>
						<Switch
							checked={useExampleData}
							onChange={(event) => setUseExampleData(event.target.checked)}
							color="primary"
						/>
					</>
				}
			/>
			{permissions && permissions.includes("*") && (
				<CardContent>
					<Grid container spacing={2} style={{ marginBottom: 20 }}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="From Date"
								type="date"
								value={dateToAnalyticsDateFormat(formValues.from)}
								onChange={handleDateChange("from")}
								fullWidth
								InputLabelProps={{
									shrink: true,
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="To Date"
								type="date"
								value={dateToAnalyticsDateFormat(formValues.to)}
								onChange={handleDateChange("to")}
								fullWidth
								InputLabelProps={{
									shrink: true,
								}}
							/>
						</Grid>
					</Grid>

					{isDataLoading ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								padding: "20px",
							}}
						>
							<CircularProgress />
						</div>
					) : dataError ? (
						<div style={{ color: "red", padding: "20px" }}>
							Error: {dataError.message}
						</div>
					) : data ? (
						<Grid container spacing={2} justifyContent="space-between">
							<Grid item xs={12} md={6}>
								<Typography variant="h6">Users</Typography>
								<Line
									data={transformAnalyticsData(data).newUsersChartData}
									options={newUsersChartOptions}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography variant="h6">Saved Projects</Typography>
								<Line
									data={transformAnalyticsData(data).savedProjectsChartData}
									options={savedProjectsChartOptions}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography variant="h6">Cart Projects</Typography>
								<Line
									data={transformAnalyticsData(data).cartProjectsChartData}
									options={cartProjectsChartOptions}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography variant="h6">Ordered Projects</Typography>
								<Line
									data={transformAnalyticsData(data).orderedProjectsChartData}
									options={orderedProjectsChartOptions}
								/>
							</Grid>
							<Grid item xs={12} md={12}>
								<Typography variant="h6">Daily Project Activity</Typography>
								<Line
									data={transformAnalyticsData(data).combinedProjectsChartData}
									options={combinedProjectsChartOptions}
								/>
							</Grid>
						</Grid>
					) : null}
				</CardContent>
			)}
		</Card>
	);
}

export { Analytics };
